import { SMART_CRM, SUITE } from 'pricing-pages-lib/constants/PricingPageRoutes';
import { useIsMobile } from 'pricing-pages-lib/js/context/IsMobileContext.tsx';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import ErrorPage from 'pricing-pages-ui/pages/ErrorPage';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { FREE } from 'self-service-api/constants/ProductLevels';
import { AllProductLines, CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import MobileRoutes from './MobileRoutes';
import { fetchCompanyDetails } from './actions/CompanyDetailsActions';
import LoadingScreen from './components/LoadingScreen';
import { makeLoadable } from './components/common/LoadableWrapper';
import { ENTERPRISE, tabIdToProductLevels } from './constants/routing/Tabs';
import { useGlobalLoading } from './hooks/loadingHooks';
import { useRecommendedRouting } from './hooks/useRecommendations';
import AppLayout from './layouts/AppLayout';
import AddOnsPage from './pages/AddOnsPage';
import BreezeIntelligencePage from './pages/BreezeIntelligencePage';
import BundlePage from './pages/BundlePage';
import CommercePage from './pages/CommercePage';
import FreeToolsPage from './pages/FreeToolsPage';
import MarketingPlusPage from './pages/MarketingPlusPage';
import ProductPage from './pages/ProductPage';
import RecommendedPage from './pages/RecommendedPage';
import SmartCrmPage from './pages/SmartCrmPage';
import SuitePage from './pages/SuitePage';
import SplashPage from './pages/splash/SplashPage';
import { isAppAuthSelector } from './selectors/AuthSelectors';
import { getIsVSBPortalSelector } from './selectors/CompanyDetailsSelectors';
import { isUngatedForRecommendedViewSelector, isUngatedForSeatsSelector } from './selectors/GatingSelectors';
import { isCompanyDetailsLoadingSelector } from './selectors/LoadingStateSelectors';
import { crmSuiteOwnershipLevel, productsFromProductInventorySelector } from './selectors/ProductInventorySelectors';
import { useSSM0009Experiment } from './utils/experiments/ssm0009';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ProductPageRoute = ({
  path,
  Component = ProductPage,
  productLine
}) => {
  const {
    isSSM0009Loading,
    isSSM0009Variant
  } = useSSM0009Experiment();
  const isAppAuth = useSelector(isAppAuthSelector);
  const sanitizedPath = path.replace(/^\/|\/$/g, '');
  const pageIncludesSSM0009Experiment = productLine && AllProductLines.includes(productLine) || sanitizedPath === SUITE || sanitizedPath === SMART_CRM;
  if (pageIncludesSSM0009Experiment) {
    if (isSSM0009Loading) {
      return /*#__PURE__*/_jsx(LoadingScreen, {});
    }
    if (isSSM0009Variant) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Route, {
          path: path,
          exact: true,
          render: () => /*#__PURE__*/_jsx(Component, {
            productLine: productLine
          })
        }), /*#__PURE__*/_jsx(Route, {
          path: `${path}/:tab?`,
          render: ({
            location
          }) => {
            return /*#__PURE__*/_jsx(Redirect, {
              to: Object.assign({}, location, {
                pathname: path
              })
            });
          }
        })]
      });
    }
  }
  if (isAppAuth) {
    return /*#__PURE__*/_jsx(Route, {
      path: path,
      render: () => /*#__PURE__*/_jsx(Component, {
        productLine: productLine
      })
    });
  }
  return /*#__PURE__*/_jsx(Route, {
    path: `${path}/:tab?`,
    render: ({
      match: {
        params: {
          tab
        }
      }
    }) => {
      if (!tab || !Object.keys(tabIdToProductLevels).includes(tab)) {
        getUsageTracker().setProperties({
          defaultedTab: ENTERPRISE
        });
        return /*#__PURE__*/_jsx(Redirect, {
          to: createToObject(`${path}/${ENTERPRISE}`)
        });
      }
      return /*#__PURE__*/_jsx(Component, {
        productLine: productLine
      });
    }
  });
};
const InterstitialPage = makeLoadable({
  loader: () => import( /* webpackChunkName: "interstitial-page" */'pricing-pages-ui/components/interstitial/InterstitialPage')
});
const DefaultInAppRoute = () => {
  const {
    recommendedRoute,
    loading: isLoadingRecommendedRoute
  } = useRecommendedRouting();
  const dispatch = useDispatch();
  useMemo(() => {
    void dispatch(fetchCompanyDetails());
  }, [dispatch]);
  const isAppReady = useGlobalLoading();
  const isCompanyDetailsLoading = useSelector(isCompanyDetailsLoadingSelector);
  const isVSBPortal = useSelector(getIsVSBPortalSelector);
  const isUngatedForSeats = useSelector(isUngatedForSeatsSelector);
  const ownedSuiteProductLevel = useSelector(crmSuiteOwnershipLevel);
  const ownedProducts = useSelector(state => {
    if (!isAppReady) return [];
    return productsFromProductInventorySelector(state);
  });
  const isFreeVSBPortal = isVSBPortal && ownedProducts && ownedProducts.length <= 0;
  const ownsSuiteProduct = isUngatedForSeats && ownedSuiteProductLevel && ownedSuiteProductLevel !== FREE;
  if (!isAppReady || isLoadingRecommendedRoute || isCompanyDetailsLoading) {
    return /*#__PURE__*/_jsx(LoadingScreen, {});
  }
  if (isFreeVSBPortal) {
    return /*#__PURE__*/_jsx(Redirect, {
      from: "/",
      exact: true,
      to: createToObject('/crm')
    });
  }
  if (ownsSuiteProduct) {
    return /*#__PURE__*/_jsx(Redirect, {
      from: "/",
      exact: true,
      to: createToObject('/suite')
    });
  }
  return /*#__PURE__*/_jsx(Redirect, {
    from: "/",
    exact: true,
    to: createToObject(`/${recommendedRoute}`)
  });
};
export default function Routes() {
  const isAppAuth = useSelector(isAppAuthSelector);
  const isSeatsPilot = useSelector(isUngatedForSeatsSelector);
  getUsageTracker().setProperties({
    isSeatsPilot
  });
  const isMobile = useIsMobile();
  const history = useHistory();
  const isUngatedForRecommendedView = useSelector(isUngatedForRecommendedViewSelector);
  if (isMobile) {
    return /*#__PURE__*/_jsx(MobileRoutes, {});
  }
  return /*#__PURE__*/_jsx(AppLayout, {
    children: /*#__PURE__*/_jsxs(Switch, {
      children: [isAppAuth && !history.location.pathname.startsWith('/upgrade') && /*#__PURE__*/_jsx(Route, {
        path: "/:page/*",
        render: ({
          location,
          match: {
            params: {
              page
            }
          }
        }) => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: Object.assign({}, location, {
              pathname: `/${page}`
            })
          });
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/interstitial",
        render: () => {
          return /*#__PURE__*/_jsx(InterstitialPage, {});
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/bundle",
        component: BundlePage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/marketing-plus",
        component: MarketingPlusPage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/commerce",
        component: CommercePage
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/suite",
        Component: SuitePage
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/splash",
        Component: SplashPage
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/sales",
        productLine: SALES
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/service",
        productLine: SERVICE
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/content",
        productLine: CMS
      }), /*#__PURE__*/_jsx(Route, {
        path: "/cms/:tab?",
        render: ({
          location,
          match: {
            params: {
              tab
            }
          }
        }) => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: Object.assign({}, location, {
              pathname: tab ? `/content/${tab}` : '/content'
            })
          });
        }
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/marketing",
        productLine: MARKETING
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/operations",
        productLine: OPERATIONS
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/smart-crm",
        Component: SmartCrmPage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/crm",
        component: FreeToolsPage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/add-ons",
        component: AddOnsPage
      }), /*#__PURE__*/_jsx(Route, {
        exact: true,
        path: "/breeze-intelligence",
        component: BreezeIntelligencePage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/recommended",
        component: isUngatedForRecommendedView ? RecommendedPage : () => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: "/"
          });
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/breeze-intelligence/*",
        render: ({
          location
        }) => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: Object.assign({}, location, {
              pathname: '/breeze-intelligence'
            })
          });
        }
      }), isAppAuth ? /*#__PURE__*/_jsx(DefaultInAppRoute, {}) : /*#__PURE__*/_jsx(Redirect, {
        from: "/",
        exact: true,
        to: createToObject('/splash')
      }), /*#__PURE__*/_jsx(Route, {
        component: ErrorPage
      })]
    })
  });
}